import type { DefaultValue } from '#layers/data-model/types/schema/default-value';
import type { GlobalCollectionId, GlobalIndexId, GlobalNamespaceId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import type {
	CreateIndexOperation,
	CreatePrimitiveFieldOperation,
	CreateRelationFieldPairOperation,
	Migration,
	MigrationOperation,
	NestedCreateCollectionOperation,
	NestedUpdateCollectionOperation,
	NestedUpdatePrimitiveFieldOperation,
} from '#layers/data-model/utils/schema/migration';

export const MIGRATION_STEP_TONE_ORDER = ['created', 'changed', 'renamed', 'deleted'] as const;
export const MIGRATION_STEP_GROUP_ORDER = ['collections', 'fields', 'relations', 'namespaces', 'indexes'] as const;

export type MigrationStepTone = typeof MIGRATION_STEP_TONE_ORDER[number];
export type MigrationStepGroupId = typeof MIGRATION_STEP_GROUP_ORDER[number];

const MIGRATION_STEP_DEFINITIONS = {
	createNamespace: { groupId: 'namespaces', tone: 'created' },
	deleteNamespace: { groupId: 'namespaces', tone: 'deleted' },
	renameNamespace: { groupId: 'namespaces', tone: 'renamed' },
	createCollection: { groupId: 'collections', tone: 'created' },
	deleteCollection: { groupId: 'collections', tone: 'deleted' },
	renameCollection: { groupId: 'collections', tone: 'renamed' },
	changeCollectionReadonly: { groupId: 'collections', tone: 'changed' },
	createField: { groupId: 'fields', tone: 'created' },
	deleteField: { groupId: 'fields', tone: 'deleted' },
	renamePrimitiveField: { groupId: 'fields', tone: 'renamed' },
	changeFieldType: { groupId: 'fields', tone: 'changed' },
	changeFieldNullability: { groupId: 'fields', tone: 'changed' },
	changeFieldListStatus: { groupId: 'fields', tone: 'changed' },
	changeFieldDefault: { groupId: 'fields', tone: 'changed' },
	changeFieldReadonly: { groupId: 'fields', tone: 'changed' },
	createIndex: { groupId: 'indexes', tone: 'created' },
	deleteIndex: { groupId: 'indexes', tone: 'deleted' },
	createRelation: { groupId: 'relations', tone: 'created' },
	deleteRelation: { groupId: 'relations', tone: 'deleted' },
	changeRelationReferentialActions: { groupId: 'relations', tone: 'changed' },
	renameRelationField: { groupId: 'relations', tone: 'renamed' },
} as const satisfies Record<string, { groupId: MigrationStepGroupId; tone: MigrationStepTone }>;

export type MigrationStepKind = keyof typeof MIGRATION_STEP_DEFINITIONS;

interface ValueChangeParams {
	fromValue: string;
	fromValueKey?: string;
	toValue: string;
	toValueKey?: string;
}

interface MigrationStepParamsByKind {
	createNamespace: { namespace: string };
	deleteNamespace: { namespace: string };
	renameNamespace: { from: string; to: string };
	createCollection: { collection: string };
	deleteCollection: { collection: string };
	renameCollection: { from: string; to: string };
	changeCollectionReadonly: { collection: string } | ({ collection: string } & ValueChangeParams);
	createField: { field: string };
	deleteField: { field: string };
	renamePrimitiveField: { from: string; to: string };
	changeFieldType: { field: string } & ValueChangeParams;
	changeFieldNullability: { field: string } & ValueChangeParams;
	changeFieldListStatus: { field: string } & ValueChangeParams;
	changeFieldDefault: { field: string } & ValueChangeParams;
	changeFieldReadonly: { field: string } & ValueChangeParams;
	createIndex: { index: string };
	deleteIndex: { index: string };
	createRelation: { relation: string; to?: string };
	deleteRelation: { relation: string };
	changeRelationReferentialActions: { relation: string };
	renameRelationField: { from: string; to: string };
}

interface MigrationStepBase<K extends MigrationStepKind> {
	id: string;
	kind: K;
	params: MigrationStepParamsByKind[K];
}

export type MigrationStep = {
	[K in MigrationStepKind]: MigrationStepBase<K>;
}[MigrationStepKind];

export interface MigrationStepGroup {
	id: MigrationStepGroupId;
	steps: MigrationStep[];
}

export interface MigrationStepResolvers {
	resolveCollectionName?: (id: GlobalCollectionId) => string | undefined;
	resolveIndexName?: (id: GlobalIndexId) => string | undefined;
	resolveNamespaceName?: (id: GlobalNamespaceId) => string | undefined;
	resolvePrimitiveFieldName?: (id: GlobalPrimitiveFieldId) => string | undefined;
	resolvePrimitiveFieldSnapshot?: (id: GlobalPrimitiveFieldId) => PrimitiveFieldSnapshot | undefined;
	resolveRelationFieldName?: (id: GlobalSingleRelationFieldId) => string | undefined;
}

const VALUE_LABEL_KEY_PREFIX = 'pages.data_model.introspection.matrix.values';

type MutableGroups = Record<MigrationStepGroupId, MigrationStep[]>;
export interface PrimitiveFieldSnapshot {
	defaultValue?: DefaultValue | null;
	isList?: boolean;
	isNullable?: boolean;
	readonly?: boolean;
	type?: PrimitiveType;
}

interface FormattedValue {
	value: string;
	key?: string;
}

export function getMigrationStepGroups(
	migration: Migration,
	resolvers: MigrationStepResolvers = {},
): MigrationStepGroup[] {
	const groups = createEmptyGroups();

	for (const operation of migration.operations) {
		addOperation(groups, operation, resolvers);
	}

	return MIGRATION_STEP_GROUP_ORDER
		.map((id) => ({
			id,
			steps: groups[id],
		}))
		.filter((group) => group.steps.length > 0);
}

function createEmptyGroups(): MutableGroups {
	return MIGRATION_STEP_GROUP_ORDER.reduce((groups, id) => {
		groups[id] = [];

		return groups;
	}, {} as MutableGroups);
}

function addOperation(
	groups: MutableGroups,
	operation: MigrationOperation,
	resolvers: MigrationStepResolvers,
) {
	switch (operation.kind) {
		case 'createNamespace':
			addStep(groups, `create-namespace-${operation.data.id}`, 'createNamespace', {
				namespace: operation.data.dbName,
			});

			break;
		case 'deleteNamespace':
			addStep(groups, `delete-namespace-${operation.data.id}`, 'deleteNamespace', {
				namespace: resolvers.resolveNamespaceName?.(operation.data.id) ?? operation.data.id,
			});

			break;
		case 'renameNamespace':
			addStep(groups, `rename-namespace-${operation.data.id}`, 'renameNamespace', {
				from: resolvers.resolveNamespaceName?.(operation.data.id) ?? operation.data.id,
				to: operation.data.dbName,
			});

			break;
		case 'createCollection': {
			const collectionName = operation.data.apiName ?? operation.data.dbName;

			addStep(groups, `create-collection-${operation.data.id}`, 'createCollection', {
				collection: collectionName,
			});

			for (const nestedOperation of operation.data.operations) {
				addCreateCollectionOperation(groups, nestedOperation, collectionName);
			}

			break;
		}
		case 'updateCollection': {
			const collectionName = resolvers.resolveCollectionName?.(operation.data.id) ?? operation.data.id;

			for (const nestedOperation of operation.data.operations) {
				addUpdateCollectionOperation(groups, nestedOperation, collectionName, resolvers);
			}

			break;
		}
		case 'deleteCollection':
			addStep(groups, `delete-collection-${operation.data.id}`, 'deleteCollection', {
				collection: resolvers.resolveCollectionName?.(operation.data.id) ?? operation.data.id,
			});

			break;
		case 'renameCollection':
			addStep(groups, `rename-collection-${operation.data.id}`, 'renameCollection', {
				from: resolvers.resolveCollectionName?.(operation.data.id) ?? operation.data.id,
				to: operation.data.apiName ?? operation.data.dbName ?? operation.data.id,
			});

			break;
		case 'createRelationFieldPair':
			addCreateRelationFieldPairOperation(groups, operation, resolvers);
			break;
		case 'updateRelationFieldPair':
			addStep(groups, `change-relation-actions-${operation.data.firstFieldId}`, 'changeRelationReferentialActions', {
				relation: resolvers.resolveRelationFieldName?.(operation.data.firstFieldId) ?? operation.data.firstFieldId,
			});

			break;
		case 'deleteRelationFieldPair':
			addStep(groups, `delete-relation-${operation.data.firstFieldId}`, 'deleteRelation', {
				relation: resolvers.resolveRelationFieldName?.(operation.data.firstFieldId) ?? operation.data.firstFieldId,
			});

			break;
		case 'renamePrimitiveField':
			addStep(groups, `rename-primitive-field-${operation.data.id}`, 'renamePrimitiveField', {
				from: resolvers.resolvePrimitiveFieldName?.(operation.data.id) ?? operation.data.id,
				to: operation.data.apiName ?? operation.data.dbName ?? operation.data.id,
			});

			break;
		case 'renameRelationField':
			addStep(groups, `rename-relation-field-${operation.data.id}`, 'renameRelationField', {
				from: resolvers.resolveRelationFieldName?.(operation.data.id) ?? operation.data.id,
				to: operation.data.apiName,
			});

			break;
		default:
			assertNever(operation);
	}
}

function addCreateCollectionOperation(
	groups: MutableGroups,
	operation: NestedCreateCollectionOperation,
	collectionName: string,
) {
	switch (operation.kind) {
		case 'createPrimitiveField':
			addCreatePrimitiveFieldStep(groups, operation, collectionName);

			break;
		case 'createIndex':
			addCreateIndexStep(groups, operation);

			break;
		default:
			assertNever(operation);
	}
}

function addUpdateCollectionOperation(
	groups: MutableGroups,
	operation: NestedUpdateCollectionOperation,
	collectionName: string,
	resolvers: MigrationStepResolvers,
) {
	switch (operation.kind) {
		case 'createPrimitiveField':
			addCreatePrimitiveFieldStep(groups, operation, collectionName);

			break;
		case 'updatePrimitiveField': {
			const fieldName = resolvers.resolvePrimitiveFieldName?.(operation.data.id) ?? `${collectionName}.${operation.data.id}`;
			const fieldSnapshot = resolvers.resolvePrimitiveFieldSnapshot?.(operation.data.id);

			for (const nestedOperation of operation.data.operations) {
				addMigrationStep(groups, getPrimitiveFieldChangeStep(nestedOperation, operation.data.id, fieldName, fieldSnapshot));
			}

			break;
		}
		case 'deletePrimitiveField':
			addStep(groups, `delete-field-${operation.data.id}`, 'deleteField', {
				field: resolvers.resolvePrimitiveFieldName?.(operation.data.id) ?? `${collectionName}.${operation.data.id}`,
			});

			break;
		case 'createIndex':
			addCreateIndexStep(groups, operation);

			break;
		case 'deleteIndex':
			addStep(groups, `delete-index-${operation.data.id}`, 'deleteIndex', {
				index: resolvers.resolveIndexName?.(operation.data.id) ?? operation.data.id,
			});

			break;
		case 'updateReadonly':
			addStep(groups, `update-readonly-${collectionName}`, 'changeCollectionReadonly', {
				collection: collectionName,
			});

			break;
		default:
			assertNever(operation);
	}
}

function addCreatePrimitiveFieldStep(
	groups: MutableGroups,
	operation: CreatePrimitiveFieldOperation,
	collectionName: string,
) {
	addStep(groups, `create-field-${operation.data.id}`, 'createField', {
		field: `${collectionName}.${operation.data.apiName ?? operation.data.dbName}`,
	});
}

function addCreateIndexStep(groups: MutableGroups, operation: CreateIndexOperation) {
	addStep(groups, `create-index-${operation.data.id}`, 'createIndex', {
		index: operation.data.dbName,
	});
}

function addCreateRelationFieldPairOperation(
	groups: MutableGroups,
	operation: CreateRelationFieldPairOperation,
	resolvers: MigrationStepResolvers,
) {
	const { firstField, secondField } = operation.data.data;
	const firstCollection = resolvers.resolveCollectionName?.(firstField.collectionId) ?? firstField.collectionId;
	const secondCollection = resolvers.resolveCollectionName?.(secondField.collectionId) ?? secondField.collectionId;

	addStep(
		groups,
		`create-relation-${firstField.id}`,
		'createRelation',
		{
			relation: `${firstCollection}.${firstField.apiName}`,
			to: `${secondCollection}.${secondField.apiName}`,
		},
	);
}

function getPrimitiveFieldChangeStep(
	operation: NestedUpdatePrimitiveFieldOperation,
	fieldId: GlobalPrimitiveFieldId,
	fieldName: string,
	fieldSnapshot?: PrimitiveFieldSnapshot,
): MigrationStep {
	switch (operation.kind) {
		case 'changeType':
			return createStep(`change-field-${fieldId}-changeType`, 'changeFieldType', withValueChangeParams(
				{ field: fieldName },
				formatPrimitiveType(fieldSnapshot?.type),
				formatPrimitiveType(operation.data.type),
			));
		case 'changeIsNullable':
			return createStep(`change-field-${fieldId}-changeIsNullable`, 'changeFieldNullability', withValueChangeParams(
				{ field: fieldName },
				formatNullable(fieldSnapshot?.isNullable),
				formatNullable(operation.data.isNullable),
			));
		case 'changeIsList':
			return createStep(`change-field-${fieldId}-changeIsList`, 'changeFieldListStatus', withValueChangeParams(
				{ field: fieldName },
				formatListStatus(fieldSnapshot?.isList),
				formatListStatus(operation.data.isList),
			));
		case 'changeDefaultValue':
			return createStep(`change-field-${fieldId}-changeDefaultValue`, 'changeFieldDefault', withValueChangeParams(
				{ field: fieldName },
				formatDefaultValue(fieldSnapshot?.defaultValue),
				formatDefaultValue(operation.data.defaultValue),
			));
		case 'changeReadonly':
			return createStep(`change-field-${fieldId}-changeReadonly`, 'changeFieldReadonly', withValueChangeParams(
				{ field: fieldName },
				formatReadonly(fieldSnapshot?.readonly),
				formatReadonly(operation.data.readonly),
			));
		default:
			return assertNever(operation);
	}
}

function addStep<K extends MigrationStepKind>(
	groups: MutableGroups,
	id: string,
	kind: K,
	params: MigrationStepParamsByKind[K],
) {
	const step = createStep(id, kind, params);

	addMigrationStep(groups, step);
}

function addMigrationStep(groups: MutableGroups, step: MigrationStep) {
	groups[MIGRATION_STEP_DEFINITIONS[step.kind].groupId].push(step);
}

function createStep<K extends MigrationStepKind>(
	id: string,
	kind: K,
	params: MigrationStepParamsByKind[K],
): Extract<MigrationStep, { kind: K }> {
	return { id, kind, params } as Extract<MigrationStep, { kind: K }>;
}

function withValueChangeParams<TParams extends Record<string, string>>(
	params: TParams,
	fromValue: FormattedValue | undefined,
	toValue: FormattedValue | undefined,
): TParams & ValueChangeParams {
	if (fromValue === undefined || toValue === undefined) {
		return {
			...params,
			fromValue: translatedValue('unknown').value,
			fromValueKey: translatedValue('unknown').key,
			toValue: toValue?.value ?? translatedValue('unknown').value,
			toValueKey: toValue?.key ?? translatedValue('unknown').key,
		};
	}

	return {
		...params,
		fromValue: fromValue.value,
		toValue: toValue.value,
		...(fromValue.key ? { fromValueKey: fromValue.key } : {}),
		...(toValue.key ? { toValueKey: toValue.key } : {}),
	};
}

function formatPrimitiveType(type: PrimitiveType | undefined): FormattedValue | undefined {
	if (!type) {
		return undefined;
	}

	return rawValue(type.name);
}

function formatNullable(value: boolean | undefined) {
	return formatBooleanValue(value, 'nullable', 'required');
}

function formatListStatus(value: boolean | undefined) {
	return formatBooleanValue(value, 'list', 'single');
}

function formatReadonly(value: boolean | undefined) {
	return formatBooleanValue(value, 'readonly', 'editable');
}

function formatBooleanValue(value: boolean | undefined, trueKey: string, falseKey: string) {
	if (value === undefined) {
		return;
	}

	return value ? translatedValue(trueKey) : translatedValue(falseKey);
}

function formatDefaultValue(value: unknown) {
	if (value === undefined) {
		return;
	}

	if (value === null) {
		return rawValue('null');
	}

	if (typeof value === 'string') {
		return rawValue(value);
	}

	return rawValue(JSON.stringify(value));
}

function rawValue(value: string): FormattedValue {
	return { value };
}

function translatedValue(value: string): FormattedValue {
	return {
		value,
		key: `${VALUE_LABEL_KEY_PREFIX}.${value}`,
	};
}

export function getMigrationStepTone(kind: MigrationStepKind): MigrationStepTone {
	return MIGRATION_STEP_DEFINITIONS[kind].tone;
}

function assertNever(value: never): never {
	throw new Error(`Unhandled migration operation: ${JSON.stringify(value)}`);
}
